import { useTranslation } from 'react-i18next';
import { getJobCommissions } from '@/utils/jobCommissions';

type JobCommissionBreakdownProps = {
  job: Parameters<typeof getJobCommissions>[0];
  variant: 'client' | 'worker';
  /** When set (e.g. transaction netAmount), prefer it for provider net display. */
  netAmountOverride?: number | string | null;
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-sm text-black font-semibold mb-1">{label}</div>
      <div className="font-normal text-black">{value}</div>
    </div>
  );
}

/**
 * Commission / discount rows for job detail and transaction popups.
 * clientCharge / workerShare = full fee; discount is applied separately.
 * Discount and voucher rows render only when values are present.
 */
export default function JobCommissionBreakdown({
  job,
  variant,
  netAmountOverride,
}: JobCommissionBreakdownProps) {
  const { t } = useTranslation();
  const c = getJobCommissions(job);
  const budget = Number(job?.budget || 0);

  if (variant === 'client') {
    return (
      <>
        <Field
          label={t('serviceAmount')}
          value={`$${budget.toFixed(2)}`}
        />

        {c.hasClientDiscount ? (
          <>
            <Field
              label={`${t('serviceChargeBeforeDiscount')} (${c.clientPercentage}%)`}
              value={`$${c.clientChargeBeforeDiscount.toFixed(2)}`}
            />
            <Field
              label={`${t('voucher_discount')} (${c.clientDiscountPercentage}%)`}
              value={`-$${c.clientDiscountAmount.toFixed(2)}`}
            />
            {c.clientVoucherCode && (
              <Field label={t('voucher_code')} value={c.clientVoucherCode} />
            )}
            <Field
              label={`${t('serviceChargeAfterDiscount')} (${c.clientChargeAfterPercentage.toFixed(1)}%)`}
              value={`$${c.clientChargeAfterDiscount.toFixed(2)}`}
            />
          </>
        ) : (
          <Field
            label={`${t('experienceUpgrade')} (${c.clientPercentage}%)`}
            value={`$${c.clientAmount.toFixed(2)}`}
          />
        )}

        <Field
          label={`${t('governmentTax')} (${c.taxPercentage}%)`}
          value={`$${c.taxAmount.toFixed(2)}`}
        />
        <Field
          label={t('totalAmount')}
          value={`$${c.totalClientCharge.toFixed(2)}`}
        />
      </>
    );
  }

  const net =
    netAmountOverride != null && netAmountOverride !== ''
      ? Number(netAmountOverride)
      : c.workerNet;

  return (
    <>
      <Field
        label={t('serviceAmount')}
        value={`$${budget.toFixed(2)}`}
      />

      {c.hasWorkerDiscount ? (
        <>
          <Field
            label={`${t('platformFeeBeforeDiscount')} (${c.workerPercentage}%)`}
            value={`$${c.workerShareBeforeDiscount.toFixed(2)}`}
          />
          <Field
            label={`${t('voucher_discount')} (${c.workerDiscountPercentage}%)`}
            value={`-$${c.workerDiscountAmount.toFixed(2)}`}
          />
          {c.providerVoucherCode && (
            <Field label={t('voucher_code')} value={c.providerVoucherCode} />
          )}
          <Field
            label={`${t('platformFeeAfterDiscount')} (${c.workerShareAfterPercentage.toFixed(1)}%)`}
            value={`$${c.workerShareAfterDiscount.toFixed(2)}`}
          />
        </>
      ) : (
        <Field
          label={`${t('businessGrowthUpgrade')} (${c.workerPercentage}%)`}
          value={`$${c.workerAmount.toFixed(2)}`}
        />
      )}

      <Field
        label={t('youWillReceive')}
        value={`$${Number(net).toFixed(2)}`}
      />
    </>
  );
}
